import React, { useState, useEffect } from 'react';
import { X, DollarSign, Save } from 'lucide-react';

interface AddSalaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (salaryData: any) => void;
  programName: string;
  className: string;
  teacherName: string;
  teacherId: number;
  monthYear: string;
  month: number;
  year: number;
  existingData?: {
    salaryId?: number;
    totalSalary?: number;
    hoursWorked?: number;
    hoursSubmitted?: number;
    salaryGivenOn?: string;
    notes?: string;
    paymentMethodId?: number;
    salaryType?: number;
    rate?: number;
    updatedOn?: string;
  };
}

const AddSalaryModal: React.FC<AddSalaryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  programName,
  className,
  teacherName,
  teacherId,
  monthYear,
  month,
  year,
  existingData
}) => {
  const [formData, setFormData] = useState({
    totalSalary: '',
    hoursWorked: '',
    salaryGivenOn: '',
    notes: '',
    paymentMethodId: '1' // Default to cash payment
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  // Get today's date in YYYY-MM-DD format for max date attribute
  const todayDate = new Date().toISOString().split('T')[0];

  // Payment method options
  const paymentMethods = [
    { value: '1', label: 'Cash' },
    { value: '2', label: 'Bank Transfer' },
    { value: '3', label: 'Check' },
    { value: '4', label: 'Online Payment' }
  ];

  // Reset form when modal opens/closes or existingData changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        totalSalary: existingData?.totalSalary?.toString() || '',
        hoursWorked: existingData?.hoursWorked?.toString() || '',
        salaryGivenOn: existingData?.salaryGivenOn || '',
        notes: existingData?.notes || '',
        paymentMethodId: existingData?.paymentMethodId?.toString() || '1'
      });
      setErrors({});
    }
  }, [isOpen, existingData]);

  // Calculate total salary when hours worked changes for hourly employees
  useEffect(() => {
    if (existingData?.salaryType === 1 && formData.hoursWorked && existingData?.rate) {
      const calculatedSalary = parseFloat(formData.hoursWorked) * existingData.rate;
      setFormData(prev => ({
        ...prev,
        totalSalary: calculatedSalary.toFixed(2)
      }));
    }
  }, [formData.hoursWorked, existingData?.salaryType, existingData?.rate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.totalSalary || parseFloat(formData.totalSalary) <= 0) {
      newErrors.totalSalary = 'Total salary must be greater than 0';
    }

    const hoursWorked = parseInt(formData.hoursWorked) || 0;
    if (hoursWorked < 0) {
      newErrors.hoursWorked = 'Hours worked cannot be negative';
    }

    if (hoursWorked > 744) { // Maximum hours in a month (31 days * 24 hours)
      newErrors.hoursWorked = 'Hours worked cannot exceed 744 hours per month';
    }

    // Validate Salary Given On date - mandatory field
    if (!formData.salaryGivenOn) {
      newErrors.salaryGivenOn = 'Salary given on date is required';
    } else {
      const salaryDate = new Date(formData.salaryGivenOn);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of today for comparison

      // Check if date is valid
      if (isNaN(salaryDate.getTime())) {
        newErrors.salaryGivenOn = 'Please enter a valid date';
      }
      // Check if date is in the future
      else if (salaryDate > today) {
        newErrors.salaryGivenOn = 'Salary date cannot be in the future';
      }
      // Optional: Check if date is too far in the past (e.g., more than 2 years)
      else {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        if (salaryDate < twoYearsAgo) {
          newErrors.salaryGivenOn = 'Salary date cannot be more than 2 years in the past';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const salaryData = {
        salaryId: existingData?.salaryId || 0,
        teacherId: teacherId,
        month: month,
        year: year,
        totalSalary: parseFloat(formData.totalSalary),
        hoursWorked: existingData?.salaryType === 1 ? parseInt(formData.hoursWorked) || 0 : 0,
        salaryGivenOn: formData.salaryGivenOn.trim(),
        notes: formData.notes.trim(),
        paymentMethodId: parseInt(formData.paymentMethodId)
      };

      await onSave(salaryData);
      onClose();
    } catch (error) {
      console.error('Error saving salary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg dark:bg-zink-600">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-8 bg-green-100 rounded-md dark:bg-green-500/20">
                <DollarSign className="size-4 text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-zink-50">
                {existingData?.salaryId ? 'Edit Salary' : 'Add Salary'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-slate-500 hover:text-slate-700 dark:text-zink-300 dark:hover:text-zink-100 disabled:opacity-50"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            
            {/* Disabled Info Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Teacher Name
                </label>
                <input
                  type="text"
                  value={`${teacherName} (#${teacherId})`}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Month/Year
                </label>
                <input
                  type="text"
                  value={monthYear}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>
            </div>

            {/* Editable Salary Fields */}
            <div className="border-t border-slate-200 dark:border-zink-500 pt-4">
              <div className="flex items-center justify-between mb-3 bg-custom-100 dark:bg-zink-600 p-2 rounded-md">
                <h4 className="text-sm font-medium text-slate-700 dark:text-zink-200">Salary Details</h4>
                <span className="text-sm italic text-slate-600 dark:text-zink-400">
                  Salary Type: {existingData?.salaryType == 1 ? 'Hourly' : 'Monthly'} 
                </span>
                <span className="text-sm italic text-slate-600 dark:text-zink-400">
                   Rate: ${existingData?.rate || '0.00'}
                </span>
              </div>
              <div className="space-y-4">
                {/* Total Salary and Hours Worked side by side */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Total Salary <span className="text-red-500">*</span>
                      {existingData?.salaryType === 1 && (
                        <span className="text-xs text-slate-500 ml-2">(Auto-calculated)</span>
                      )}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-slate-500 dark:text-zink-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.totalSalary}
                        onChange={(e) => handleInputChange('totalSalary', e.target.value)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:text-zink-100 ${
                          errors.totalSalary 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-slate-200 dark:border-zink-500'
                        } ${
                          existingData?.salaryType === 1 
                            ? 'bg-slate-50 text-slate-500 dark:bg-zink-700 dark:text-zink-400' 
                            : ''
                        }`}
                        placeholder="0.00"
                        disabled={isLoading || existingData?.salaryType === 1}
                      />
                    </div>
                    {errors.totalSalary && (
                      <p className="text-red-500 text-xs mt-1">{errors.totalSalary}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Hours Worked
                      {existingData?.hoursSubmitted && existingData.hoursSubmitted > 0 && (
                        <span className="text-xs text-slate-500 dark:text-zink-400 ml-2">
                          (Submitted: {existingData.hoursSubmitted})
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="744"
                      value={existingData?.salaryType === 2 ? '0' : formData.hoursWorked}
                      onChange={(e) => handleInputChange('hoursWorked', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:text-zink-100 ${
                        errors.hoursWorked 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-slate-200 dark:border-zink-500'
                      } ${
                        existingData?.salaryType === 2 
                          ? 'bg-slate-50 text-slate-500 dark:bg-zink-700 dark:text-zink-400' 
                          : ''
                      }`}
                      placeholder="0"
                      disabled={isLoading || existingData?.salaryType === 2}
                    />
                    {errors.hoursWorked && (
                      <p className="text-red-500 text-xs mt-1">{errors.hoursWorked}</p>
                    )}
                  </div>
                </div>

                {/* Payment Method and Salary Given On side by side */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={formData.paymentMethodId}
                      onChange={(e) => handleInputChange('paymentMethodId', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                      disabled={isLoading}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Salary Given On <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.salaryGivenOn}
                      onChange={(e) => handleInputChange('salaryGivenOn', e.target.value)}
                      max={todayDate}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:text-zink-100 ${
                        errors.salaryGivenOn 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-slate-200 dark:border-zink-500'
                      }`}
                      disabled={isLoading}
                      required
                    />
                    {errors.salaryGivenOn && (
                      <p className="text-red-500 text-xs mt-1">{errors.salaryGivenOn}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                    placeholder="Additional notes..."
                    disabled={isLoading}
                  />
                </div>

                {/* Last Updated Date - only show if updatedOn exists */}
                {existingData?.updatedOn && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-zink-700 rounded-md border border-slate-200 dark:border-zink-500">
                    <div className="flex items-center gap-2">
                      <DollarSign className="size-4 text-slate-500 dark:text-zink-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-zink-200">
                        Last Updated:
                      </span>
                      <span className="text-sm text-slate-600 dark:text-zink-300">
                        {(() => {
                          const date = new Date(existingData.updatedOn);
                          const dateStr = date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          });
                          const timeStr = date.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          });
                          return `${dateStr} at ${timeStr}`;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 dark:border-zink-500">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-zink-700 dark:text-zink-200 dark:border-zink-500 dark:hover:bg-zink-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4 mr-2" />
                  {existingData?.salaryId ? 'Update Salary' : 'Save Salary'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSalaryModal;

